import { useEffect, useRef } from "react";
import axios from "axios";
import { isStaffBrowser } from "../lib/staffCookie";

const API = process.env.REACT_APP_BACKEND_URL;

// localStorage key for the cross-session visitor UUID. One per
// browser; lets us count distinct visitors without identifying them.
const VISITOR_KEY = "medicys_visitor_id";

// Per-page-load session UUID, regenerated on every mount so a single
// browser visiting twice = two sessions.
const _newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36));

// Pause heartbeats if the user has been idle (no mousemove / keydown /
// scroll / touch) for this long. Prevents "left the tab open for 6
// hours" inflating per-tab dwell totals.
const IDLE_MS = 60_000;

// Cadence of the heartbeat. The backend caps each beat at 5 min so
// our 15s isn't load-bearing, but the lower it is, the snappier the
// "currently viewing" indicator on the staff dashboard feels.
const HEARTBEAT_MS = 15_000;

/**
 * Tracks open / tab_change / heartbeat / close events for a public
 * share-token viewer session. Mounts inside `PublicProposal.jsx`.
 *
 * Also returns a `recordEvent(type, opts)` function so the host
 * component can fire custom engagement events from anywhere in the
 * proposal viewer:
 *   recordEvent("download",      { meta: { format: "xlsx" } })
 *   recordEvent("sample_change", { meta: { block_id, country, value } })
 *   recordEvent("fx_switch",     { meta: { from: "GBP", to: "USD", rate } })
 *
 * Pass the current `tab` key as it changes - the hook fires a
 * `tab_change` event AND retargets subsequent heartbeats at the new
 * tab so per-tab dwell totals stay accurate.
 *
 * Pass `source="staff-preview"` when this is a staff opening the
 * proposal from the dashboard (use a `?as=staff` query param on the
 * /p/{token} URL). Those events are excluded from client-facing
 * dashboards by default.
 */
export function useShareAnalytics(token, tab, source = "client", enabled = true) {
  // Resolve the effective source: explicit ?as=staff (passed in as
  // `source`) wins, else if this browser has visited an internal
  // page recently the ms_staff cookie is set and we self-tag as
  // staff-preview. The backend ALSO double-checks the cookie on
  // every track call so even if the hook is bypassed (e.g. a
  // crafted request) we still won't pollute client metrics.
  const effectiveSource =
    source === "staff-preview" || isStaffBrowser() ? "staff-preview" : "client";
  // Refs (not state) so the heartbeat closure stays stable across
  // tab changes and React StrictMode double-invokes the effect
  // without firing a duplicate `open` event.
  const sessionIdRef = useRef(null);
  const visitorIdRef = useRef(null);
  const lastBeatAtRef = useRef(Date.now());
  const lastInteractionRef = useRef(Date.now());
  const tabRef = useRef(tab);
  const prevTabRef = useRef(null);
  const openedRef = useRef(false);
  const openedAtRef = useRef(0);

  // Keep the latest tab in a ref so the heartbeat closure (set up
  // once on mount) reads the up-to-date value rather than capturing
  // the initial tab forever.
  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  // Fire the actual network request. Uses sendBeacon for `close`
  // (page unload, beacon survives the navigation) and axios for
  // everything else.
  const send = (event_type, opts = {}) => {
    if (!token || !sessionIdRef.current) return;
    const payload = {
      event_type,
      tab: opts.tab ?? tabRef.current ?? null,
      session_id: sessionIdRef.current,
      visitor_id: visitorIdRef.current,
      dwell_ms: opts.dwell_ms ?? 0,
      source: effectiveSource,
    };
    if (opts.meta && typeof opts.meta === "object") {
      payload.meta = opts.meta;
    }
    const url = `${API}/api/public/proposals/${encodeURIComponent(token)}/track`;
    if (event_type === "close" && typeof navigator.sendBeacon === "function") {
      // sendBeacon is fire-and-forget and survives `unload`.
      try {
        const blob = new Blob([JSON.stringify(payload)], {
          type: "application/json",
        });
        navigator.sendBeacon(url, blob);
        return;
      } catch {
        /* fall through to axios */
      }
    }
    // Best-effort axios call - silent on failure (analytics must
    // never break the client-facing viewer).
    axios.post(url, payload, { timeout: 5000 }).catch(() => {});
  };

  // Stable callback the host component can wire to button clicks,
  // sample-input commits, currency dropdowns, etc. Wrapped in a ref
  // so consumers don't trigger re-renders when send identity changes.
  const sendRef = useRef(send);
  useEffect(() => { sendRef.current = send; });
  const recordEvent = (event_type, opts = {}) => sendRef.current(event_type, opts);

  // --------------------------------------------------------------
  // Mount: ensure IDs, fire `open`, install listeners + heartbeat.
  // --------------------------------------------------------------
  useEffect(() => {
    // `enabled` gates the whole tracker: for gated links it stays
    // false until the email gate is satisfied, so we never record
    // an "open" for someone who only ever saw the gate screen (and
    // the first event fires WITH the gate cookie, stamping the
    // session with the verified email immediately).
    if (!token || !enabled) return undefined;

    // Pull-or-create the visitor UUID. Stored in localStorage so the
    // same browser visiting another proposal stitches into a single
    // visitor identity.
    try {
      const stored = window.localStorage.getItem(VISITOR_KEY);
      if (stored) {
        visitorIdRef.current = stored;
      } else {
        const fresh = _newId();
        window.localStorage.setItem(VISITOR_KEY, fresh);
        visitorIdRef.current = fresh;
      }
    } catch {
      // Private mode / disabled storage - fall back to a per-load
      // visitor id (still distinguishes sessions, just can't tell
      // repeat visits apart).
      visitorIdRef.current = _newId();
    }

    lastBeatAtRef.current = Date.now();
    lastInteractionRef.current = Date.now();

    // Open ONCE per token - React StrictMode's dev remount reuses
    // the same session instead of minting a ghost one (which showed
    // up as duplicate "Opened the proposal" lines + inflated visits).
    if (openedRef.current !== token) {
      sessionIdRef.current = _newId();
      openedRef.current = token;
      openedAtRef.current = Date.now();
      send("open");
    }

    // Mark the user "interacting" on any of these - pauses idle
    // gating and keeps the heartbeats flowing.
    const interact = () => {
      lastInteractionRef.current = Date.now();
    };
    const onVisibility = () => {
      if (document.hidden) {
        // Flush whatever dwell has accumulated since the last beat
        // before the user backgrounds the tab. Without this, a quick
        // background → reload would lose the final 15 s.
        const now = Date.now();
        const idle = now - lastInteractionRef.current > IDLE_MS;
        if (!idle) {
          const delta = Math.min(now - lastBeatAtRef.current, HEARTBEAT_MS * 4);
          if (delta > 1000) send("heartbeat", { dwell_ms: delta });
          lastBeatAtRef.current = now;
        }
      } else {
        // Coming back to the tab - reset the heartbeat clock so we
        // don't bill the away-time as dwell on the next tick.
        lastBeatAtRef.current = Date.now();
        lastInteractionRef.current = Date.now();
      }
    };
    const onUnload = () => {
      const now = Date.now();
      // Sub-1.5s lifetimes are StrictMode's dev unmount, not a real
      // exit - suppress the ghost close event.
      if (now - openedAtRef.current < 1500) return;
      const delta = Math.min(now - lastBeatAtRef.current, HEARTBEAT_MS * 4);
      send("close", { dwell_ms: delta > 1000 ? delta : 0 });
    };
    document.addEventListener("mousemove", interact, { passive: true });
    document.addEventListener("keydown", interact);
    document.addEventListener("scroll", interact, { passive: true });
    document.addEventListener("touchstart", interact, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);

    const beat = setInterval(() => {
      // Skip while the tab is backgrounded or the user has been
      // idle past the threshold; we'll resume on the next interact.
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastInteractionRef.current > IDLE_MS) return;
      const delta = now - lastBeatAtRef.current;
      if (delta < 1000) return;
      send("heartbeat", { dwell_ms: delta });
      lastBeatAtRef.current = now;
    }, HEARTBEAT_MS);

    return () => {
      // On real unmount (route change, not StrictMode), flush a
      // final close + tear listeners down.
      onUnload();
      clearInterval(beat);
      document.removeEventListener("mousemove", interact);
      document.removeEventListener("keydown", interact);
      document.removeEventListener("scroll", interact);
      document.removeEventListener("touchstart", interact);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, effectiveSource, enabled]);

  // --------------------------------------------------------------
  // Tab change: fire a `tab_change` and rebase the heartbeat clock
  // so the NEXT heartbeat attributes its dwell to the NEW tab, not
  // a sliver to the old one. Uses its own prevTabRef because
  // tabRef is synced by an earlier effect and would already equal
  // `tab` by the time this one runs.
  // --------------------------------------------------------------
  useEffect(() => {
    if (!openedRef.current || !tab) return;
    if (prevTabRef.current === null) {
      // Initial mount - `open` already carried the first tab.
      prevTabRef.current = tab;
      return;
    }
    const prev = prevTabRef.current;
    if (prev !== tab) {
      // Flush whatever time was accrued on the OLD tab to that tab
      // before re-pointing the heartbeat at the new one.
      const now = Date.now();
      const delta = now - lastBeatAtRef.current;
      if (delta > 1000) {
        send("heartbeat", { tab: prev, dwell_ms: delta });
        lastBeatAtRef.current = now;
      }
      send("tab_change", { tab });
      prevTabRef.current = tab;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return { recordEvent };
}
